import { useState } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import MuiLink from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ImageIcon from '@mui/icons-material/Image'
import type { Child, Document, Manifest } from '../../api/authoring'
import { useGetAuthoringComponentQuery, useGetManifestQuery } from '../../api/authoring'
import { problemMessage } from '../../api/baseApi'
import { useIntent, shown } from '../../api/useIntent'
import { thumbnail } from '../../model/derived'
import { acceptsUpload, proseOf, typeOf } from '../../model/manifest'
import { nameOrId } from '../../model/name'
import { choosableReferences, heldUnder, keyAndLabel, members, recordAddress, recordPath, type RecordAddress } from '../../model/records'
import { routes } from '../../model/routes'
import { moved } from '../../ui/reorder'
import { Loading, QueryError } from '../../ui/QueryState'
import { NewRecordDialog } from './NewRecordDialog'

/**
 * One listed record type as the site's owner meets it: "the albums".
 *
 * The list is the Collection the type names, in its order, because that
 * is the order the site shows them in. A new one is made here and joins
 * that Collection on the server, so the owner never meets the Collection
 * itself, nor the Gallery an album owns, nor the route that gives each
 * one a page (finishing-patterns §11).
 *
 * **Tiles where the records have a picture, rows where they do not, and
 * the manifest decides which.** This screen was a table for every type,
 * and for a photographer's albums that was the wrong shape: a 36px
 * thumbnail in a row reduces the one thing that identifies an album to a
 * decoration beside its name. An owner does not recognise "Water-crafts"
 * by the word — they recognise the picture, and a table's density is
 * exactly the thing that will not let the picture get big enough to be
 * recognised. A grid of ~200px covers is what the comp asks for and what
 * the content is.
 *
 * **Asked of the manifest, never of the type's name.** `Library` already
 * answers the same question well — a type whose components hold a file is
 * browsed as a thumbnail grid and everything else as a list, decided by
 * `acceptsUpload` — and the question there is right for the same reason
 * it is right here: "is there anything to look at" is exactly what
 * decides whether looking is how a reader finds a thing. The equivalent
 * for a *record* is one step further out, because a record does not hold
 * a file, it points at one: a type is pictured when one of its
 * `choosableReferences` — the relations an owner may aim at the record's
 * own things, which is how the cover is found two lines below — accepts a
 * type that `acceptsUpload`. So the album is pictured because its
 * declaration says `cover: Media` and Media holds a file, and a record
 * type declared tomorrow with a cover is a grid tomorrow with no change
 * here. A special case for `custom/album` would have been three fewer
 * lines and would have made the next agency's type a table forever.
 *
 * It is the *type's declaration* that decides, not whether the records
 * happen to have covers set yet. A type with covers declared and none
 * chosen draws a grid of placeholders, which is right: the empty frames
 * are the screen telling an owner that a cover is a thing they may set,
 * where a table would simply never mention it.
 *
 * **The table is unchanged for a type with no picture**, and the reasons
 * it was a table still hold there. Each row used to be a picture, a name,
 * a `flexGrow` spacer and two arrows hard against the right edge, which
 * on a wide screen put the control that moves a row as far as it is
 * possible to get from the row's name — worst of all for a list whose
 * entire purpose is its *order*. Columns pull them back to a fixed,
 * scannable position and line every address up under the one above it.
 *
 * The addresses stay in both shapes, because they are the one thing that
 * distinguishes two albums an owner named similarly, and read down a
 * column — or across a grid — they are how a duplicate or a bad slug is
 * spotted.
 *
 * **What the grid costs, and what pays for it.** A table states its order
 * in one direction; a grid that wraps states it in two, and "third" is
 * something a reader has to count out. So each tile carries its position
 * as a number, which a table never needed, and its move controls point
 * *along the reading order* — left and right — rather than up and down,
 * because on a wrapping grid an up arrow on the second row honestly reads
 * as "put it in the row above". Same intent, same payload, same two
 * clicks per position; only the arrow's direction follows the layout it
 * is drawn in.
 */
export function ContentRecords() {
  const { tenant = '', site = '', type = '' } = useParams()
  const navigate = useNavigate()
  const manifest = useGetManifestQuery({ tenant })
  const declared = typeOf(manifest.data, type)
  const collectionId = declared?.collection ?? ''
  const collection = useGetAuthoringComponentQuery({ tenant, id: collectionId }, { skip: !collectionId })
  const website = useGetAuthoringComponentQuery({ tenant, id: site })
  const { send, isLoading: sending, error: sendError } = useIntent(tenant)
  const [creating, setCreating] = useState(false)

  if (manifest.isLoading || collection.isLoading) return <Loading label="Reading the list…" />
  if (!declared?.collection) {
    return <Alert severity="info">This kind of content is not kept as a list here.</Alert>
  }
  if (collection.error) return <QueryError error={collection.error} title="Could not read the list" />

  const prose = proseOf(declared, type)
  const pictured = hasPicture(manifest.data, type)
  const listed = members(collection.data)
  const order = listed.map((child) => child.linkId)
  const address = recordAddress(website.data, collectionId)

  const move = (linkId: string, direction: -1 | 1) => {
    const at = order.indexOf(linkId)
    const target = order[at + direction]
    if (target === undefined) return
    const next = moved(order, linkId, target)
    if (next) send(collectionId, 'collection.items_reordered', { order: next }).catch(shown)
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Breadcrumbs>
          <MuiLink component={RouterLink} to={routes.site(tenant, site)}>
            {website.data?.label ?? 'Site'}
          </MuiLink>
          <Typography color="text.primary">{prose.plural}</Typography>
        </Breadcrumbs>
        <Stack direction="row" spacing={2} alignItems="flex-end" justifyContent="space-between" flexWrap="wrap">
          <Stack spacing={0.5}>
            <Typography variant="h1">{prose.plural}</Typography>
            <Typography color="text.secondary">
              {/* A type's description is a phrase as often as a
                  sentence, and the line after it is a sentence: it is
                  closed, so the two do not run together. */}
              {prose.description ? `${prose.description}${/[.!?]$/.test(prose.description) ? '' : '.'} ` : ''}
              {address
                ? `Each one has its own page at ${address.pattern.replace(/:[^/]+$/, '…')}, in the order below.`
                : 'Shown on the site in the order below.'}
            </Typography>
          </Stack>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            New {prose.title.toLowerCase()}
          </Button>
        </Stack>
      </Stack>

      {sendError && <Alert severity="error">{problemMessage(sendError, 'That change was not made.')}</Alert>}

      {listed.length === 0 ? (
        /* One empty state for both shapes. What is missing is the same
           thing whichever way the present ones would have been drawn,
           and it is a sentence rather than a grid of nothing. */
        <Paper variant="outlined">
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">
              There are no {prose.plural.toLowerCase()} yet. A new one appears here, and on the site once it is
              published.
            </Typography>
          </Box>
        </Paper>
      ) : pictured ? (
        /* `minmax(200px, 1fr)` is `Library`'s grid and the comp's: wide
           enough that a cover is recognisable at a glance, narrow enough
           that a ten-album site is one screenful rather than a scroll.
           Nothing here is told how many columns there are — the count
           falls out of the width, which is what makes the same markup
           right at 1280 and at 1600. */
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
          {listed.map((child, index) => (
            <RecordTile
              key={child.linkId}
              tenant={tenant}
              site={site}
              type={type}
              child={child}
              manifest={manifest.data}
              address={address}
              position={index + 1}
              total={listed.length}
              busy={sending}
              onMove={(direction) => move(child.linkId, direction)}
            />
          ))}
        </Box>
      ) : (
        <Paper variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                {/* The picture's column is headed by nothing: it is how a
                    row is recognised, not a fact anybody sorts or scans
                    by, and "Image" over a strip of thumbnails is a label
                    that says what the reader can already see. The name
                    column takes the type's own word for one of these. */}
                <TableCell sx={{ width: 56 }} />
                <TableCell sx={{ width: '40%' }}>{prose.title}</TableCell>
                <TableCell>Address</TableCell>
                <TableCell align="right" sx={{ width: 96 }}>
                  Order
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listed.map((child, index) => (
                <RecordRow
                  key={child.linkId}
                  tenant={tenant}
                  site={site}
                  type={type}
                  child={child}
                  manifest={manifest.data}
                  address={address}
                  first={index === 0}
                  last={index === listed.length - 1}
                  busy={sending}
                  onMove={(direction) => move(child.linkId, direction)}
                />
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <NewRecordDialog
        tenant={tenant}
        typeName={type}
        manifest={manifest.data}
        address={address}
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(created) => {
          setCreating(false)
          navigate(routes.record(tenant, site, type, created.id))
        }}
      />
    </Stack>
  )
}

/**
 * Whether records of this type carry a picture, and so whether this
 * screen is a grid or a table.
 *
 * **Two manifest questions, no names.** `choosableReferences` is already
 * how a record's cover is found — the single, not-owned relations an
 * owner may aim at one of the record's own things — and each one states
 * the types it `accepts`, resolved. `acceptsUpload` says which of those
 * hold a file, which is the same question `Library` asks to decide
 * between its grid and its list. A type is pictured when the two meet.
 *
 * It answers false for a type that declares no such relation, which is
 * the ordinary record: a news item with a title, a date and some prose
 * has nothing to look at, and a grid of it would be a grid of grey
 * rectangles with the words shrunk into a corner. That type keeps the
 * table exactly as it was.
 */
function hasPicture(manifest: Manifest | undefined, type: string): boolean {
  return choosableReferences(manifest, type).some((reference) =>
    reference.accepts.some((accepted) => acceptsUpload(manifest, accepted)),
  )
}

/**
 * The picture a record has chosen: the first choosable relation it holds
 * that resolved to something holding a file — an album's cover.
 *
 * Read off the *record's* references rather than the type's declaration,
 * because the declaration says one may be chosen and only the document
 * says whether one was. Both shapes ask this, so they ask it once.
 */
function coverOf(manifest: Manifest | undefined, type: string, record: Document | undefined): string | undefined {
  const held = choosableReferences(manifest, type)
    .map((reference) => heldUnder(record?.references, reference.name))
    .find((reference) => reference?.type && acceptsUpload(manifest, reference.type))
  return held?.id
}

/**
 * One record as a tile: the cover, the name, the address, and its place
 * in the order.
 *
 * **The whole card is the link, except the controls.** A tile is one
 * thing to a reader, so the whole of it should open the record — but a
 * button nested inside an anchor is neither valid nor operable, so the
 * cover and the words are the link and the order strip beneath the rule
 * is not. That strip is the only part of the tile that is not "go to
 * this album", which is exactly the line the divider is drawing.
 *
 * **The address is kept even when there is none.** An empty mono line
 * holds the tile's height steady in a grid where the alternative is a
 * row of cards that do not line up, and it says the true thing: this one
 * has no page yet because it has no key.
 */
function RecordTile({
  tenant,
  site,
  type,
  child,
  manifest,
  address,
  position,
  total,
  busy,
  onMove,
}: {
  tenant: string
  site: string
  type: string
  child: Child
  manifest: Manifest | undefined
  address: RecordAddress | undefined
  /** Where this one sits in the Collection's order, counting from one. */
  position: number
  total: number
  busy: boolean
  onMove: (direction: -1 | 1) => void
}) {
  const id = child.id ?? ''
  const record = useGetAuthoringComponentQuery({ tenant, id })
  const { key } = keyAndLabel(manifest, type)
  const fields = (record.data?.fields ?? {}) as Record<string, unknown>
  const path = recordPath(address, key ? (fields[key] as string | undefined) : undefined)
  const cover = coverOf(manifest, type, record.data)
  const name = record.data ? nameOrId(record.data, manifest) : '…'

  return (
    <Paper
      variant="outlined"
      sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', '&:hover': { borderColor: 'primary.main' } }}
    >
      <Box
        component={RouterLink}
        to={routes.record(tenant, site, type, id)}
        sx={{
          display: 'block',
          textDecoration: 'none',
          color: 'inherit',
          flexGrow: 1,
          '&:hover .record-name': { textDecoration: 'underline' },
        }}
      >
        {/* 4:3, which is the comp's and is the shape a photograph most
            often already is — a square would crop a landscape cover to
            the middle third of itself, and the cover is the point. */}
        <Picture tenant={tenant} id={cover} ratio="4 / 3" />
        <Stack spacing={0.25} sx={{ px: 1.25, pt: 1, pb: 1 }}>
          <Typography className="record-name" variant="body2" fontWeight={600} noWrap title={name}>
            {name}
          </Typography>
          <Typography
            variant="caption"
            noWrap
            sx={{ fontFamily: 'ui-monospace, monospace', color: path ? 'text.secondary' : 'text.disabled' }}
          >
            {path ?? 'No address yet'}
          </Typography>
          {record.data?.tombstoned && (
            <Typography variant="caption" color="error">
              Deleted. It shows nothing on the site.
            </Typography>
          )}
        </Stack>
      </Box>
      <Divider />
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pl: 1.25, pr: 0.5, py: 0.25 }}>
        {/* The number a table never needed. Down a column, "third" is
            where the eye already is; across a grid that wraps at
            whatever the window allows, it is something a reader would
            otherwise have to count, and counting is the one thing this
            screen exists to save them. */}
        <Typography variant="caption" color="text.secondary">
          {position} of {total}
        </Typography>
        <Box sx={{ whiteSpace: 'nowrap' }}>
          <Tooltip title="Move earlier">
            <span>
              <IconButton size="small" disabled={busy || position === 1} onClick={() => onMove(-1)}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move later">
            <span>
              <IconButton size="small" disabled={busy || position === total} onClick={() => onMove(1)}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Stack>
    </Paper>
  )
}

function RecordRow({
  tenant,
  site,
  type,
  child,
  manifest,
  address,
  first,
  last,
  busy,
  onMove,
}: {
  tenant: string
  site: string
  type: string
  child: Child
  manifest: Manifest | undefined
  address: RecordAddress | undefined
  first: boolean
  last: boolean
  busy: boolean
  onMove: (direction: -1 | 1) => void
}) {
  const id = child.id ?? ''
  const record = useGetAuthoringComponentQuery({ tenant, id })
  const { key } = keyAndLabel(manifest, type)
  const fields = (record.data?.fields ?? {}) as Record<string, unknown>
  const path = recordPath(address, key ? (fields[key] as string | undefined) : undefined)
  // The record's cover, where its type declares one at all. Drawn small,
  // because in a row it is a mark beside a name rather than the thing
  // being read — a type whose picture *is* the identifying fact is a
  // grid, and never reaches this row.
  const cover = coverOf(manifest, type, record.data)

  return (
    <TableRow>
      {/* Smaller than the 56 it was drawn at loose: at a table's density
          a thumbnail that sets the row height stops being a mark beside
          a name and starts being the row. */}
      <TableCell sx={{ width: 56, pr: 0 }}>
        <Picture tenant={tenant} id={cover} size={36} />
      </TableCell>
      <TableCell>
        <Stack spacing={0.25}>
          <MuiLink component={RouterLink} to={routes.record(tenant, site, type, id)} sx={{ fontWeight: 500 }}>
            {record.data ? nameOrId(record.data, manifest) : '…'}
          </MuiLink>
          {/* Kept under the name rather than given a column, because it
              is the one thing on this screen that is true of almost no
              row: a column for it would be empty down its whole length
              and the sentence would have nowhere to fit. */}
          {record.data?.tombstoned && (
            <Typography variant="caption" color="error">
              Deleted. It shows nothing on the site.
            </Typography>
          )}
        </Stack>
      </TableCell>
      <TableCell sx={{ fontFamily: 'ui-monospace, monospace', color: 'text.secondary', fontSize: '0.75rem' }}>
        {path}
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <Tooltip title="Move up">
          <span>
            <IconButton size="small" disabled={busy || first} onClick={() => onMove(-1)}>
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move down">
          <span>
            <IconButton size="small" disabled={busy || last} onClick={() => onMove(1)}>
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  )
}

/**
 * A Media's thumbnail, or a placeholder where there is none yet.
 *
 * **A square mark by default, and a tile's cover where a ratio is
 * given.** One component rather than two, because everything that is
 * hard here is the same either way: which rendition exists is a fact the
 * *document* carries, so this reads it rather than assembling a URL from
 * a width, and the absence has ordinary causes a reader can act on —
 * which is the placeholder. What changes with a ratio is only the box:
 * full width, the caller's aspect, and square corners because it sits
 * flush against the edges of a card rather than beside a name.
 *
 * The default is untouched on purpose: the callers that pass a `size`
 * and nothing else — this file's table, the record screen's owned
 * children — get exactly the box they got before.
 */
export function Picture({
  tenant,
  id,
  size = 56,
  ratio,
}: {
  tenant: string
  id?: string
  size?: number
  /** A CSS `aspect-ratio`, for a picture that fills its container's width. */
  ratio?: string
}) {
  const media = useGetAuthoringComponentQuery({ tenant, id: id ?? '' }, { skip: !id })
  // 640 for a tile: a ~200px cover is ~400 device pixels on the screens
  // this is read on, and 320 is visibly soft at that size.
  const url = thumbnail(media.data, ratio ? 640 : size * 2)
  return (
    <Box
      sx={{
        ...(ratio
          ? { width: '100%', aspectRatio: ratio, borderRadius: 0 }
          : { width: size, height: size, borderRadius: 1 }),
        flexShrink: 0,
        bgcolor: 'action.hover',
        display: 'grid',
        placeItems: 'center',
        overflow: 'hidden',
      }}
    >
      {url ? (
        <Box
          component="img"
          src={url}
          alt=""
          loading="lazy"
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <ImageIcon color="disabled" fontSize={ratio ? 'large' : 'medium'} />
      )}
    </Box>
  )
}
